import { combineReducers } from 'redux';

import { ProposalCreatorAction, ProposalCreatorActionKeys } from './actions';
import { FileError } from '../ImportManager/actions';

export enum ProposalSource {
	TEMPLATE = 'TEMPLATE',
	PROPOSAL = 'PROPOSAL',
	FILE = 'FILE',
}

export enum ProposalCreationStatus {
	IMPORTING = 'importing',
	PROCESSING = 'processing',
}

export enum LoadingStatus {
	NOT_LOADED = 'NOT_LOADED',
	LOADING = 'LOADING',
	LOADED = 'LOADED',
}

export interface ProposalCreationProgress {
	status?: ProposalCreationStatus | null;
	percentage?: number;
	error?: FileError | null;
}

export interface SelectedTemplate {
	id: number;
	label: string;
	isCustomTemplate?: boolean;
}

export interface ProposalCreatorState {
	source: ProposalSource | null;
	isDisplayed: boolean;
	requestId: string | null;
	fromTemplate: {
		masterProposalsIds: number[];
		customTemplatesIds: number[];
		loadingStatus: LoadingStatus;
		selectedTemplate: SelectedTemplate | null;
		name: string;
		error: string | null;
	};
	fromFile: ProposalCreationProgress;
}

const creationProgressInitialState: ProposalCreationProgress = {
	status: null,
	percentage: 0,
	error: null,
};

const source = (state: ProposalSource | null = null, action: ProposalCreatorAction): ProposalSource | null => {
	switch (action.type) {
		case ProposalCreatorActionKeys.SHOW:
			return action.source;
		case ProposalCreatorActionKeys.HIDE:
			return null;
		default:
			return state;
	}
};

const masterProposalsIds = (state: number[] = [], action: ProposalCreatorAction): number[] => {
	switch (action.type) {
		case ProposalCreatorActionKeys.SET_PROPOSAL_TEMPLATES:
			return action.masterProposals.map(({ id }) => id);
		case ProposalCreatorActionKeys.HIDE:
			return [];
		default:
			return state;
	}
};

const customTemplatesIds = (state: number[] = [], action: ProposalCreatorAction): number[] => {
	switch (action.type) {
		case ProposalCreatorActionKeys.SET_PROPOSAL_TEMPLATES:
			return action.customTemplates.map(({ id }) => id);
		case ProposalCreatorActionKeys.REMOVE_TEMPLATE_FROM_STORE:
			return state.filter(id => id !== action.id);
		case ProposalCreatorActionKeys.HIDE:
			return [];
		default:
			return state;
	}
};

const loadingStatus = (state: LoadingStatus = LoadingStatus.NOT_LOADED, action: ProposalCreatorAction): LoadingStatus => {
	switch (action.type) {
		case ProposalCreatorActionKeys.GET_PROPOSAL_TEMPLATES:
			return LoadingStatus.LOADING;
		case ProposalCreatorActionKeys.SET_PROPOSAL_TEMPLATES:
			return LoadingStatus.LOADED;
		case ProposalCreatorActionKeys.HIDE:
			return LoadingStatus.NOT_LOADED;
		default:
			return state;
	}
};

const selectedTemplate = (state: SelectedTemplate | null = null, action: ProposalCreatorAction): SelectedTemplate | null => {
	switch (action.type) {
		case ProposalCreatorActionKeys.SELECT_TEMPLATE:
			return action.data;
		case ProposalCreatorActionKeys.HIDE:
			return null;
		default:
			return state;
	}
};

const isDisplayed = (state: boolean = false, action: ProposalCreatorAction): boolean => {
	switch (action.type) {
		case ProposalCreatorActionKeys.SHOW:
			return true;
		case ProposalCreatorActionKeys.HIDE:
			return false;
		default:
			return state;
	}
};

const requestId = (state: string | null = null, action: ProposalCreatorAction): string | null => {
	switch (action.type) {
		case ProposalCreatorActionKeys.SET_REQUEST_ID:
			return action.requestId;
		case ProposalCreatorActionKeys.REMOVE_FROM_CREATION_PROGRESS:
		case ProposalCreatorActionKeys.HIDE:
			return null;
		default:
			return state;
	}
};

const name = (state: string = '', action: ProposalCreatorAction): string => {
	switch (action.type) {
		case ProposalCreatorActionKeys.SET_NAME:
			return action.name;
		case ProposalCreatorActionKeys.SELECT_TEMPLATE:
			return `${action.data.label}-1`;
		case ProposalCreatorActionKeys.HIDE:
			return '';
		default:
			return state;
	}
};

const error = (state: string | null = null, action: ProposalCreatorAction): string | null => {
	switch (action.type) {
		case ProposalCreatorActionKeys.SET_ERROR:
			return action.error;
		case ProposalCreatorActionKeys.SELECT_TEMPLATE:
		case ProposalCreatorActionKeys.SET_NAME:
		case ProposalCreatorActionKeys.HIDE:
			return null;
		default:
			return state;
	}
};

const creationProgress = (state: ProposalCreationProgress = creationProgressInitialState, action: ProposalCreatorAction): ProposalCreationProgress => {
	switch (action.type) {
		case ProposalCreatorActionKeys.SET_CREATION_STATUS:
			return {
				...state,
				status: action.status,
				percentage: 0,
			};
		case ProposalCreatorActionKeys.UPDATE_CREATION_PROGRESS:
			if (!state.status) {
				return state;
			}
			return {
				...state,
				...action.data,
			};
		case ProposalCreatorActionKeys.REMOVE_FROM_CREATION_PROGRESS:
		case ProposalCreatorActionKeys.HIDE:
			return creationProgressInitialState;
		default:
			return state;
	}
};

export const proposalCreator = combineReducers<ProposalCreatorState>({
	source,
	isDisplayed,
	requestId,
	fromTemplate: combineReducers({
		masterProposalsIds,
		customTemplatesIds,
		loadingStatus,
		selectedTemplate,
		name,
		error,
	}),
	fromFile: creationProgress,
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/ProposalCreator/reducer.ts